import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthentication } from '../../../../common/hooks/useAuthentication';
import { ICart } from '../../../../container/cart/ICart';
import { CartProps, useCartProvider } from '../../../../providers/cart-provider';
import { useDevice } from '../../../../providers/device-provider';
import { useUserProfile } from '../../../../providers/user-provider';
import Button from '../../../basic/button';
import Icon from '../../../basic/icons';
import ErrorMessage from '../../errorMessage';
import { NotificationDialog } from './headerComponents/header-steps';
import {
  buildAnalytics,
  DataAnalyticsLocations,
  DataAnalyticsTypes,
} from '../../../basic/analytics';
import { getSession } from '../../../../utils/sessionStorageUtils';

export enum NavToolbarIcons {
  ShowAfterLogin = 'showafterlogin',
  Authenticated = 'authenticated',
  Cart = 'cart',
  Logout = 'Logout',
  Notification = 'Notifications',
  None = 'None',
}

interface NavToolbarProps {
  readonly toolMap?: any;
  readonly exclude?: string[];
  readonly iconOnly?: boolean;
}

export default function NavMenuToolbar({ toolMap, exclude, iconOnly }: NavToolbarProps) {
  const { userProfile } = useUserProfile();
  const { isMobile } = useDevice();
  const { error, setError, loginRoute } = useAuthentication();
  const { t } = useTranslation();
  const { cart } = useCartProvider() as CartProps;
  const { cartItems }: ICart = cart || { cartItems: [] };
  const toolbar = toolMap?.signoutToolbar;
  const isLoggedIn = getSession('isLoggedIn');
  const { loginError } = useUserProfile();
  // Show the logged-in header when profile data exists or the session indicates a logged-in user
  // outside the login route and there is no login error.
  const showLoggedInHeaderView =
    userProfile ??
    (!loginError &&
      isLoggedIn &&
      isLoggedIn === 'true' &&
      location.pathname.split('/')[2] !== 'login');

  const handleClick = (icon: any) => {
    if (icon === NavToolbarIcons.Cart) {
      sessionStorage.setItem('cartActionForADA', '');
    }
    if (icon === 'signIn') {
      loginRoute();
    }
  };

  return (
    <div className="flex items-center justify-end" role="toolbar">
      <ErrorMessage visible={error?.length > 0} setVisible={setError} modal message={t(error)} />
      {toolbar?.map((list: any, idx: any) => (
        <Fragment key={idx}>
          {list?.map((tool: any, idx: any) => {
            if (exclude?.includes(tool.label)) return <div key={idx}></div>;
            if (tool.displayrule === NavToolbarIcons.ShowAfterLogin && !showLoggedInHeaderView) {
              return;
            }

            const analytics = buildAnalytics(
              DataAnalyticsLocations.Header,
              DataAnalyticsTypes.Button,
            );

            return (
              <span key={`hd-dt-tbr-${idx}`}>
                {tool.displayrule === NavToolbarIcons.Authenticated && !showLoggedInHeaderView && (
                  <Button
                    link={tool.path}
                    key={`hd-dt-tbl-${idx}`}
                    onClick={() => handleClick(tool.icon)}
                    className="flex flex-col items-center justify-center group"
                    dataAnalytics={analytics}
                    data-testid={`buttonLinks_${idx}`}
                  >
                    <div className="relative ">
                      {isMobile && (
                        <Icon name={tool.icon} className="text-2xl md:text-2xl text-white" />
                      )}

                      {!isMobile && (
                        <span
                          className={`flex justify-center items-center rounded-full border border-solid py-2 px-5 mr-md text-sm ${
                            tool?.componentId === 'register'
                              ? 'text-white border-white'
                              : 'bg-btn-fill text-card-header'
                          } text-btn-base border-btn-border hover:bg-btn-hover hover:text-btn-hover-text w-ful`}
                        >
                          <Icon
                            name={tool.icon}
                            className={`text-md md:text-md mr-1 ${
                              tool.background !== 'null' ? '' : 'text-white '
                            }`}
                          />
                          {tool.label}
                        </span>
                      )}
                    </div>

                    {!iconOnly && (
                      <span className="font-semibold text-xs group-hover:text-secondary-300 text-white whitespace-nowrap mt-1 px-4">
                        {tool.label}
                      </span>
                    )}
                  </Button>
                )}

                {tool.displayrule === NavToolbarIcons.ShowAfterLogin && showLoggedInHeaderView && isMobile && (
                  tool.icon === NavToolbarIcons.Notification ? (
                    <NotificationDialog
                      data={tool}
                      divider={false}
                      notificationErrorMsg={[]}
                      stacked
                    />
                  ) : (
                    <Button
                      link={tool.path}
                      key={`hd-dt-tbl-${idx}`}
                      onClick={() => handleClick(tool.icon)}
                      className="flex flex-col items-center justify-center group"
                      dataAnalytics={buildAnalytics(
                        DataAnalyticsLocations.Header,
                        DataAnalyticsTypes.Button,
                      )}
                      tabIndex={-1}
                      data-testid={`buttonLinksMobile_${idx}`}
                    >
                      <div className="relative ">
                        {tool.icon === NavToolbarIcons.Cart && cartItems?.length > 0 && (
                          <span className="flex justify-center items-center absolute right-3 top-[-5px] pt-px rounded-full w-4 h-4 text-white bg-rose-600 text-2xs">
                            {cart?.cartItemsCount}
                          </span>
                        )}
                        <Icon name={tool.icon} className="text-2xl md:text-2xl text-white" />
                      </div>

                      <span className="font-semibold text-xs group-hover:text-secondary-300 text-white whitespace-nowrap mt-1 px-4">
                        {tool.label}
                      </span>
                    </Button>
                  )
                )}
              </span>
            );
          })}
        </Fragment>
      ))}
    </div>
  );
}
